// Utilidades compartidas de las pantallas de Inventario (catálogo + precios).
// CRUD directo a PostgREST vía el atajo /rest/inventario.<tabla> (el interceptor
// añade Accept-Profile/Content-Profile).
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const base = (t: string) => `/rest/inventario.${t}`;
const repr = { headers: { Prefer: 'return=representation' } };

export const inv = {
  list: async <T,>(t: string, query = ''): Promise<T[]> =>
    (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) =>
    (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) =>
    (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) =>
    (await apiClient.delete(`${base(t)}?id=eq.${id}`)).data,
};

// Estilos de campos (consistentes con el componente Input)
export const fieldCls =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function Campo({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block space-y-1 ${className}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled = false }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className={`flex items-center gap-2 text-sm select-none ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-input" />
      {label}
    </label>
  );
}

// Regla del proyecto (2026-10-10): sin ventanas modales. «Modal» se conserva por
// compatibilidad, pero muestra un panel integrado en la página.
export function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return <PanelIntegrado title={title} onClose={onClose} wide={wide}>{children}</PanelIntegrado>;
}

// ---- Tipos ----
export interface Categoria {
  id: string; parent_id: string | null; codigo: string | null; nombre: string;
  descripcion: string | null; orden: number; es_activo: boolean;
}
export interface Producto {
  id: string; categoria_id: string | null; codigo: string; nombre: string; descripcion: string | null;
  tipo_producto: string; naturaleza: string | null;
  es_vendible: boolean; es_comprable: boolean; es_facturable: boolean;
  controla_stock: boolean; maneja_lote: boolean; maneja_caducidad: boolean; requiere_receta: boolean; apto_tv: boolean;
  es_regulado?: boolean; es_esencial?: boolean; es_estacional?: boolean; es_nuevo?: boolean;
  unidad_medida: string; presentacion: string | null; fracciones: number;
  codigo_barras1: string | null; codigo_barras2: string | null; codigo_barras3: string | null;
  precio_base: number; costo_referencial: number; moneda: string; iva_tarifa: number;
  principio_activo: string | null; concentracion: string | null; marca_id: string | null;
  codigo_tarifario: string | null; codigo_cum: string | null; es_activo: boolean;
  parent_id?: string | null; tiene_variantes?: boolean;
  participa_debito?: boolean; participa_credito?: boolean; solo_efectivo?: boolean;
}
export interface Marca {
  id: string; codigo: string | null; nombre: string; descripcion: string | null; es_activo: boolean;
  clasificacion: 'propia' | 'rebate' | 'terceros';
}
export const CLASIF_MARCA: { value: Marca['clasificacion']; label: string }[] = [
  { value: 'terceros', label: 'Terceros' },
  { value: 'propia', label: 'Marca propia' },
  { value: 'rebate', label: 'Rebate (acuerdo proveedor)' },
];
export interface AjusteMotivo {
  id: string; codigo: string | null; nombre: string; tipo: 'entrada' | 'salida' | 'ambos'; es_activo: boolean;
}
export const TIPO_MOTIVO: { value: AjusteMotivo['tipo']; label: string }[] = [
  { value: 'ambos', label: 'Ambos (entrada y salida)' },
  { value: 'entrada', label: 'Entrada (sobrante)' },
  { value: 'salida', label: 'Salida (merma, etc.)' },
];
export interface ListaPrecio {
  id: string; codigo: string | null; nombre: string; sentido: string; partner_id: string | null;
  prioridad: number; comision_pct: number | null; comision_fija: number | null;
  vigente_desde: string | null; vigente_hasta: string | null; moneda: string; es_activo: boolean;
}
export interface Precio {
  id: string; lista_id: string; producto_id: string; regla: string;
  precio: number | null; porcentaje: number | null; es_activo: boolean;
}
export interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[]; subtipo_proveedor?: string }

export const TIPO_PRODUCTO = ['fisico', 'servicio', 'medicamento', 'paquete'];
export const NATURALEZA = ['insumo', 'medicamento', 'servicio_medico', 'examen', 'procedimiento', 'hospitalizacion', 'equipo', 'otro'];
export const SENTIDO = ['venta', 'compra'];
export const REGLA = ['fijo', 'descuento_pct', 'markup_pct'];

export const partnersList = async (): Promise<PartnerOpt[]> =>
  (await apiClient.post('/rpc/sri_partners', {}, { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } })).data || [];

// ===========================================================================
// Inventario Fase 2: bodegas, stock, kardex, recepción y transferencias
// ===========================================================================
export const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
export const INV = { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } };

// Llamada a una RPC del esquema inventario
export const invRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, INV)).data;

// Parseo numérico defensivo para <input type="number"> (evita NaN al borrar el campo)
export const toNum = (v: string | number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Invalida todas las queries de inventario (saldos, kardex, valorización, reportes,
// documentos) tras una operación que mueve stock.
export const invalidateInventario = (qc: QueryClient): void => {
  void qc.invalidateQueries({
    predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('inv-'),
  });
};

// Formateadores
export const num = (v: number | string | null, d = 2) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { minimumFractionDigits: d, maximumFractionDigits: d });
export const money = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

// ---- Ordenamiento de tablas (asc/desc por columna) -------------------------
// Mismo patrón que features/control-bancario/shared.tsx (SortTh clicable).
export type SortDir = 'asc' | 'desc';
export interface SortState { key: string; dir: SortDir; toggle: (k: string) => void }
export function useSort<T>(rows: T[], accessor: (row: T, key: string) => string | number | null,
                           initialKey = '', initialDir: SortDir = 'asc') {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState<SortDir>(initialDir);
  const toggle = (k: string) => { if (k === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setKey(k); setDir('asc'); } };
  const sorted = useMemo(() => {
    if (!key) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = accessor(a, key); const vb = accessor(b, key);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // nulos al final
      if (vb == null) return -1;
      const c = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
      return dir === 'asc' ? c : -c;
    });
    return arr;
  }, [rows, key, dir, accessor]);
  return { sorted, key, dir, toggle } as { sorted: T[] } & SortState;
}

export function SortTh({ children, col, sort, align = 'left', className = '' }:
  { children: ReactNode; col: string; sort: SortState; align?: 'left' | 'right' | 'center'; className?: string }) {
  const active = sort.key === col;
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`py-2 pr-3 ${alignCls} cursor-pointer select-none hover:text-foreground ${className}`}
        onClick={() => sort.toggle(col)} title={i18n.t('inventario:comun.ordenar')}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        <span className={`text-[10px] leading-none ${active ? 'text-foreground' : 'text-muted-foreground/40'}`}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}

export const TIPO_LOCATION = ['principal', 'devolucion', 'sucursal', 'transito', 'cuarentena', 'otro'];

// ---- Tipos ----
export interface Location {
  id: string; codigo: string | null; nombre: string; tipo: string;
  es_principal: boolean; permite_negativo: boolean; unidad_venta?: string;
  direccion: string | null; telefono: string | null; responsable: string | null;
  observaciones: string | null; es_activo: boolean;
  latitud: number | null; longitud: number | null; supervisor_id: string | null;
  clasificacion: string | null; ventas_12m: number; tipo_descuento: string | null;
}
export interface ProductoDescuento {
  id?: string; producto_id?: string; letra: string; descuento_pct: number;
  promo_pct: number | null; promo_desde: string | null; promo_hasta: string | null;
  promo_hora_inicio: string | null; promo_hora_fin: string | null; promo_dias: number[] | null;
  vol1_cant: number | null; vol1_pct: number | null;
  vol2_cant: number | null; vol2_pct: number | null;
  vol3_cant: number | null; vol3_pct: number | null;
}
// 0=domingo … 6=sábado (misma convención que extract(dow))
export const DIAS_SEMANA_MIN = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
export const LETRAS_DESCUENTO = ['A', 'B', 'C', 'D', 'E', 'F'];
export interface ClasificacionVenta {
  id: string; letra: string; descripcion: string | null;
  monto_desde: number; monto_hasta: number | null; orden: number; es_activo: boolean;
}
export const clasificacionesList = (): Promise<ClasificacionVenta[]> =>
  inv.list<ClasificacionVenta>('clasificacion_ventas', '?select=*&order=orden,letra');
export interface UsuarioLocation {
  id: string; usuario_id: string; location_id: string;
  puede_recibir: boolean; puede_transferir: boolean; puede_ajustar: boolean; es_activo: boolean;
}
export interface UsuarioOpt { id: string; username: string; email: string | null }
export interface Lote {
  id: string; producto_id: string; numero_lote: string;
  fecha_fabricacion: string | null; fecha_caducidad: string | null; registro_sanitario: string | null;
}
export interface ProductoOpt {
  id: string; codigo: string; nombre: string;
  controla_stock: boolean; maneja_lote: boolean; maneja_caducidad: boolean; unidad_medida: string;
  presentacion?: string | null; fracciones?: number | null;
  precio_base?: number | null; iva_tarifa?: number | null; costo_referencial?: number | null;
}
export interface TicketBuyOpt {
  id: string; serie_comprobante: string | null; razon_social_emisor: string | null;
  fecha_emision: string | null; importe_total: number | null; partner_id: string | null; documento_id: string;
  subtotal: number | null; iva: number | null;
}
export interface PrepLinea {
  numero_linea: number; codigo_principal: string | null; codigo_auxiliar: string | null;
  descripcion: string; cantidad: number | null; costo_unitario: number | null;
  producto_id: string | null; producto_nombre: string | null;
  producto_maneja_lote: boolean | null; producto_maneja_caducidad: boolean | null;
  match_origen: string | null;
}
export interface RecepcionPrep {
  tickets_buy_id: string; partner_id: string | null; referencia: string | null;
  location_principal_id: string | null; lineas: PrepLinea[];
}

// ---- Combos ----
export const locationsList = (): Promise<Location[]> =>
  inv.list<Location>('locations', '?select=*&es_activo=eq.true&order=nombre');
export const productosOptList = (): Promise<ProductoOpt[]> =>
  inv.list<ProductoOpt>('productos', '?select=id,codigo,nombre,controla_stock,maneja_lote,maneja_caducidad,unidad_medida,presentacion,fracciones,precio_base,iva_tarifa,costo_referencial&es_activo=eq.true&tiene_variantes=eq.false&order=nombre');

// Ubicaciones (bins) activas de una bodega, GENERAL primero.
export interface UbicacionOpt { id: string; codigo: string; nombre: string | null; es_default: boolean }
export const ubicacionesList = (bodegaId: string): Promise<UbicacionOpt[]> =>
  (bodegaId
    ? inv.list<UbicacionOpt>('ubicaciones', `?bodega_id=eq.${bodegaId}&es_activa=eq.true&select=id,codigo,nombre,es_default&order=es_default.desc,codigo`)
    : Promise.resolve([]));
export const usuariosList = async (): Promise<UsuarioOpt[]> =>
  (await apiClient.get<UsuarioOpt[]>('/rest/autenticacion.usuarios?select=id,username,email&order=username')).data;
export const lotesByProducto = (productoId: string): Promise<Lote[]> =>
  inv.list<Lote>('lotes', `?select=*&producto_id=eq.${productoId}&order=fecha_caducidad.nullslast`);
export const ticketsBuyList = async (): Promise<TicketBuyOpt[]> =>
  (await apiClient.get<TicketBuyOpt[]>(
    '/rest/financiero.tickets_buy?select=id,serie_comprobante,razon_social_emisor,fecha_emision,importe_total,partner_id,documento_id,subtotal,iva&order=fecha_emision.desc&limit=300'
  )).data;

// Recepciones internas confirmadas (para precargar transferencias)
export interface RecepcionDoc { id: string; codigo: string | null; fecha: string; location_id: string; referencia: string | null; partner_id: string | null }
export interface RecepLineaDoc { producto_id: string; lote_id: string | null; cantidad: number }
// Solo recepciones confirmadas AÚN NO usadas en una transferencia (para precargar).
export const recepcionesConfirmadas = (): Promise<RecepcionDoc[]> =>
  invRpc<RecepcionDoc[]>('inv_recepciones_para_transferir', {});
export const recepcionLineasDoc = (recepcionId: string): Promise<RecepLineaDoc[]> =>
  inv.list<RecepLineaDoc>('recepcion_lineas', `?select=producto_id,lote_id,cantidad&recepcion_id=eq.${recepcionId}`);

// ---- Imagen del producto (tabla satélite inventario.producto_imagenes) ----
export interface ProductoImagen { producto_id: string; mime: string | null; thumb: string | null; imagen: string | null }

export const getProductoImagen = async (id: string): Promise<ProductoImagen | null> =>
  (await apiClient.get<ProductoImagen[]>(`/rest/inventario.producto_imagenes?producto_id=eq.${id}&select=producto_id,mime,thumb,imagen`)).data[0] ?? null;

export const upsertProductoImagen = async (id: string, data: { mime: string; thumb: string; imagen: string }) =>
  (await apiClient.post('/rest/inventario.producto_imagenes', { producto_id: id, ...data, updated_at: new Date().toISOString() },
    { headers: { Prefer: 'resolution=merge-duplicates' } })).data;

export const deleteProductoImagen = async (id: string) =>
  (await apiClient.delete(`/rest/inventario.producto_imagenes?producto_id=eq.${id}`)).data;

// ===========================================================================
// Búsqueda de producto (combo reutilizable) — mismo patrón "práctico" de
// Recepcion.tsx: typeahead por palabras, sin tildes, sobre código+nombre.
// ===========================================================================

// Normaliza para buscar sin tildes ni mayúsculas.
export const normBuscar = (s: string): string => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Cualquier catálogo de producto compatible (ProductoOpt u otro con estos 3 campos).
export interface ProductoBuscable { id: string; codigo: string; nombre: string }

/**
 * Combo buscable de producto (typeahead por palabras, código+nombre, sin tildes).
 * Funciona dentro de celdas de tabla: el desplegable va en un portal (position
 * fixed) para que un contenedor con scroll (overflow-x-auto también clipa el eje Y)
 * no lo recorte. Click fuera cierra; las opciones usan onMouseDown para registrar
 * el click antes del blur/cierre.
 */
export function BuscadorProducto<T extends ProductoBuscable>({
  value, onChange, productos, placeholder, disabled, compact, filtro, allowClear,
}: {
  value: string; onChange: (id: string) => void; productos: T[];
  placeholder?: string; disabled?: boolean; compact?: boolean;
  filtro?: (p: T) => boolean; allowClear?: boolean;
}) {
  const { t } = useTranslation('inventario');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const base = useMemo(() => (filtro ? productos.filter(filtro) : productos), [productos, filtro]);
  const sel = productos.find((p) => p.id === value) ?? null;

  const posicionar = () => { if (inputRef.current) setRect(inputRef.current.getBoundingClientRect()); };
  const abrir = () => { if (disabled) return; setQ(''); setActiveIdx(0); posicionar(); setOpen(true); };

  useEffect(() => {
    if (!open) return;
    const upd = () => posicionar();
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    document.addEventListener('mousedown', fuera);
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd); document.removeEventListener('mousedown', fuera); };
  }, [open]);

  const filtrados = useMemo(() => {
    const words = normBuscar(q).split(/\s+/).filter(Boolean);
    if (!words.length) return base.slice(0, 60);
    return base.filter((p) => { const t = normBuscar(`${p.codigo} ${p.nombre}`); return words.every((w) => t.includes(w)); }).slice(0, 60);
  }, [q, base]);

  const elegir = (id: string) => { onChange(id); setQ(''); setOpen(false); };

  const alturaCls = compact ? 'h-9 text-sm' : '';

  return (
    <div className="relative">
      <input
        ref={inputRef}
        className={`${fieldCls} ${alturaCls} ${allowClear && value && !open ? 'pr-7' : ''}`}
        value={open ? q : (sel ? `${sel.codigo} · ${sel.nombre}` : '')}
        placeholder={placeholder ?? t('comun.buscar_producto')}
        disabled={disabled}
        onFocus={abrir}
        onChange={(e) => { setQ(e.target.value); setActiveIdx(0); if (!open) { posicionar(); setOpen(true); } }}
        onKeyDown={(e) => {
          if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') abrir(); return; }
          if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtrados.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); const p = filtrados[activeIdx]; if (p) elegir(p.id); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {allowClear && value && !open && !disabled && (
        <button type="button" aria-label={t('comun.limpiar')} title={t('comun.limpiar')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
          onMouseDown={(e) => { e.preventDefault(); onChange(''); }}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && rect && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 280) }}
          className="z-[60] max-h-64 overflow-auto rounded border bg-background shadow-lg text-sm">
          {filtrados.length === 0 && <div className="px-2 py-2 text-muted-foreground">{t('comun.sin_coincidencias')}</div>}
          {filtrados.map((p, i) => (
            <button key={p.id} type="button"
              className={`block w-full px-2 py-1 text-left hover:bg-muted ${p.id === value ? 'bg-muted/60' : ''} ${i === activeIdx ? 'bg-muted' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); elegir(p.id); }}>
              <span className="font-mono text-xs">{p.codigo}</span> · {p.nombre}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}

// ===========================================================================
// Recepción asistida — Ola A (semáforo de rentabilidad por línea + editor PVP
// + historial de compra). RPCs del esquema inventario (ver invRpc).
// ===========================================================================
export type Semaforo = 'rojo' | 'ambar' | 'verde';

export interface DesgloseLetra {
  letra: string | null;
  desc_pct: number; origen: string; precio_neto: number; pvp_con_iva: number;
  margen_valor: number; margen_pct: number; markup_pct: number; bajo_costo: boolean; semaforo: Semaforo;
}
export interface DesgloseSinDescuento { precio_neto: number; pvp_con_iva: number; margen_pct: number; semaforo: Semaforo }
export interface DesglosePeor { letra: string | null; margen_pct: number | null; semaforo: Semaforo; bajo_costo: boolean }

// ---- Recepción asistida — Ola B (ABC/rentabilidad, sugerencia de precio,
// erosión de margen, renegociación por deriva de costo, multiproveedor) -----
export type RedondeoEstilo = '99' | '95' | '50' | '00' | 'none';
export const REDONDEO_OPCIONES: { value: RedondeoEstilo; label: string }[] = [
  { value: '99', label: 'Terminación .99' },
  { value: '95', label: 'Terminación .95' },
  { value: '50', label: 'Terminación .50' },
  { value: '00', label: 'Entero (.00)' },
  { value: 'none', label: 'Sin redondeo' },
];
export interface AbcInfo {
  clase: string; clase_xyz: string; dias_cobertura: number | null; es_lento: boolean;
}
export interface SugerenciaPrecio {
  margen_objetivo: number; origen_objetivo: 'categoria' | 'empresa';
  redondeo_estilo: RedondeoEstilo;
  pvp_neto_sugerido: number; pvp_con_iva_sugerido: number;
  pvp_con_iva_final: number; pvp_neto_final: number; margen_post_redondeo: number;
}
export interface ErosionInfo {
  en_erosion: boolean; margen_actual: number; costo_anterior: number | null;
  margen_objetivo: number; precio_base_actual: number;
}
export interface RenegociacionInfo {
  deriva: 'al_alza' | 'estable' | 'a_la_baja'; al_alza: boolean; a_la_baja: boolean; sube_sistematico: boolean;
}
export interface MultiproveedorInfo {
  n_proveedores: number; mejor: { proveedor: string; costo: number } | null;
}
export interface RecepcionDesglose {
  letras: DesgloseLetra[]; sin_descuento: DesgloseSinDescuento; peor: DesglosePeor;
  usa_peor_local: boolean; evaluar_con_descuento: boolean;
  mayorista: boolean; abc: AbcInfo | null; sugerencia: SugerenciaPrecio;
  erosion: ErosionInfo; renegociacion: RenegociacionInfo; multiproveedor: MultiproveedorInfo;
}

// Resumen de cabecera de una recepción en curso (inv_recepcion_resumen).
export interface RecepcionResumen {
  lineas_total: number; mapeadas: number; alerta_costo: number; alerta_rentabilidad: number; impacto_valor: number;
}

// Override de margen objetivo por familia (inv_categoria_margen_get/set).
export interface CategoriaMargen { categoria_id: string; categoria: string; margen_objetivo: number; updated_at: string }

export interface HistorialCompra { fecha: string; documento: string; cantidad: number; costo_unitario: number }
export interface HistorialResumen {
  min_costo: number; max_costo: number; prom_costo: number; ultimo_costo: number;
  mejor: { costo: number; proveedor: string } | null;
}
export interface HistorialTendenciaPunto { semana: string; costo: number }
export interface HistorialTendencia {
  semanas_con_compra: number; costo_inicial: number; costo_final: number;
  acumulado_pct: number; deriva: string; serie: HistorialTendenciaPunto[];
}
export interface RecepcionHistorial {
  a_este_proveedor: HistorialCompra[]; a_cualquiera: HistorialCompra[];
  resumen: HistorialResumen; tendencia: HistorialTendencia;
}

export interface RentabilidadConfigData {
  umbral_rojo: number; umbral_ambar: number;
  evaluar_con_descuento: boolean; usa_peor_local: boolean;
  margen_objetivo: number; redondeo_estilo: RedondeoEstilo; mayorista_desc_ref: number;
}

// Clientes mayoristas reales de la empresa (para "Ver precio como" en Recepción).
export interface MayoristaItem { partner_id: string; nombre: string; descuento_general: number }

// Descuento BASE por letra de local (inv_producto_descuentos_get/_set).
export interface ProductoDescuentoLocal { letra: string; descuento_pct: number; locales: string[] }

export const SEMAFORO_CLS: Record<Semaforo, string> = {
  rojo: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  ambar: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  verde: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
};
export const SEMAFORO_LABEL: Record<Semaforo, string> = { rojo: 'ROJO', ambar: 'ÁMBAR', verde: 'VERDE' };

// Debounce genérico (previews de precio/desglose sin disparar un POST por tecla).
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// Redimensiona una imagen en el navegador (canvas) a un data URL JPEG liviano.
export async function resizeToDataURL(file: File, max: number, quality = 0.7): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error(i18n.t('inventario:comun.err_leer_imagen')));
      i.src = url;
    });
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error(i18n.t('inventario:comun.err_canvas'));
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}
